import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const textVariants = cva("", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
      xl: "text-xl",
      "2xl": "font-text-2xl",
      "sm-leading-none": "font-text-sm-leading-none",
      "sm-leading-normal": "font-text-sm-leading-normal",
    },
    weight: {
      normal: "normal",
      medium: "medium",
      semibold: "semibold",
      bold: "bold",
    },
    color: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      primary: "text-primary-foreground",
      secondary: "text-secondary-foreground",
      accent: "text-accent-foreground",
      destructive: "text-destructive-foreground",
    },
  },
  compoundVariants: [
    // xs variants
    {
      size: "xs",
      weight: "normal",
      className:
        "font-[number:var(--text-xs-normal-font-weight)] text-[length:var(--text-xs-normal-font-size)] leading-[var(--text-xs-normal-line-height)] tracking-[var(--text-xs-normal-letter-spacing)] [font-style:var(--text-xs-normal-font-style)]",
    },
    {
      size: "xs",
      weight: "medium",
      className:
        "font-[number:var(--text-xs-medium-font-weight)] text-[length:var(--text-xs-medium-font-size)] leading-[var(--text-xs-medium-line-height)] tracking-[var(--text-xs-medium-letter-spacing)] [font-style:var(--text-xs-medium-font-style)]",
    },
    {
      size: "xs",
      weight: "semibold",
      className:
        "font-[number:var(--text-xs-semibold-font-weight)] text-[length:var(--text-xs-semibold-font-size)] leading-[var(--text-xs-semibold-line-height)] tracking-[var(--text-xs-semibold-letter-spacing)] [font-style:var(--text-xs-semibold-font-style)]",
    },
    // sm variants - using medium as default since there's no text-sm-normal
    {
      size: "sm",
      weight: "normal",
      className:
        "font-[number:var(--text-sm-leading-normal-normal-font-weight)] text-[length:var(--text-sm-leading-normal-normal-font-size)] leading-[var(--text-sm-leading-normal-normal-line-height)] tracking-[var(--text-sm-leading-normal-normal-letter-spacing)] [font-style:var(--text-sm-leading-normal-normal-font-style)]",
    },
    {
      size: "sm",
      weight: "medium",
      className:
        "font-[number:var(--text-sm-medium-font-weight)] text-[length:var(--text-sm-medium-font-size)] leading-[var(--text-sm-medium-line-height)] tracking-[var(--text-sm-medium-letter-spacing)] [font-style:var(--text-sm-medium-font-style)]",
    },
    {
      size: "sm",
      weight: "semibold",
      className:
        "font-[number:var(--text-sm-semibold-font-weight)] text-[length:var(--text-sm-semibold-font-size)] leading-[var(--text-sm-semibold-line-height)] tracking-[var(--text-sm-semibold-letter-spacing)] [font-style:var(--text-sm-semibold-font-style)]",
    },
    // sm-leading-normal variant
    {
      size: "sm-leading-normal",
      weight: "normal",
      className:
        "font-[number:var(--text-sm-leading-normal-normal-font-weight)] text-[length:var(--text-sm-leading-normal-normal-font-size)] leading-[var(--text-sm-leading-normal-normal-line-height)] tracking-[var(--text-sm-leading-normal-normal-letter-spacing)] [font-style:var(--text-sm-leading-normal-normal-font-style)]",
    },
    // base variants
    {
      size: "base",
      weight: "normal",
      className:
        "font-[number:var(--text-base-normal-font-weight)] text-[length:var(--text-base-normal-font-size)] leading-[var(--text-base-normal-line-height)] tracking-[var(--text-base-normal-letter-spacing)] [font-style:var(--text-base-normal-font-style)]",
    },
    {
      size: "base",
      weight: "medium",
      className:
        "font-[number:var(--text-base-medium-font-weight)] text-[length:var(--text-base-medium-font-size)] leading-[var(--text-base-medium-line-height)] tracking-[var(--text-base-medium-letter-spacing)] [font-style:var(--text-base-medium-font-style)]",
    },
    {
      size: "base",
      weight: "semibold",
      className:
        "font-[number:var(--text-base-semibold-font-weight)] text-[length:var(--text-base-semibold-font-size)] leading-[var(--text-base-semibold-line-height)] tracking-[var(--text-base-semibold-letter-spacing)] [font-style:var(--text-base-semibold-font-style)]",
    },
    // xl variants
    {
      size: "xl",
      weight: "medium",
      className:
        "font-[number:var(--text-xl-medium-font-weight)] text-[length:var(--text-xl-medium-font-size)] leading-[var(--text-xl-medium-line-height)] tracking-[var(--text-xl-medium-letter-spacing)] [font-style:var(--text-xl-medium-font-style)]",
    },
    {
      size: "xl",
      weight: "bold",
      className:
        "font-[number:var(--text-xl-bold-font-weight)] text-[length:var(--text-xl-bold-font-size)] leading-[var(--text-xl-bold-line-height)] tracking-[var(--text-xl-bold-letter-spacing)] [font-style:var(--text-xl-bold-font-style)]",
    },
    // 2xl variants
    {
      size: "2xl",
      weight: "medium",
      className:
        "font-[number:var(--text-2xl-medium-font-weight)] text-[length:var(--text-2xl-medium-font-size)] leading-[var(--text-2xl-medium-line-height)] tracking-[var(--text-2xl-medium-letter-spacing)] [font-style:var(--text-2xl-medium-font-style)]",
    },
    {
      size: "2xl",
      weight: "bold",
      className:
        "font-[number:var(--text-2xl-bold-font-weight)] text-[length:var(--text-2xl-bold-font-size)] leading-[var(--text-2xl-bold-line-height)] tracking-[var(--text-2xl-bold-letter-spacing)] [font-style:var(--text-2xl-bold-font-style)]",
    },
  ],
  defaultVariants: {
    size: "base",
    weight: "normal",
    color: "default",
  },
});

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "color">,
    VariantProps<typeof textVariants> {
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div" | "label";
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ className, size, weight, color, as = "span", children, ...props }, ref) => {
    const Component = as;
    const textColor: "default" | "muted" | "primary" | "secondary" | "accent" | "destructive" | null | undefined =
      color || "default";
    return (
      <Component
        ref={ref as any}
        className={cn(textVariants({ size, weight, color: textColor }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Text.displayName = "Text";

export { Text, textVariants };

